import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logInfo, logError } from "../../utils/logger";
import { Formik, Form, Field } from "formik";
import { toast } from 'react-toastify';
import {
  addPaymentMethodToClinic,
  removePaymentMethodFromClinic,
  copyPaymentMethodsBetweenClinics,
  fetchClinics,
  fetchClinicById
} from "../../store/thunks/clinicThunk";
import {
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ChevronUpDownIcon,
  CheckIcon
} from "@heroicons/react/20/solid";
import { Listbox, Transition, Dialog } from "@headlessui/react";
import { getAddressLabels } from "../../applicationUi/components/AddressFields";
import { usStates } from "../../constants/usStates";

const canadianProvinces = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "YT", label: "Yukon" },
];

const countries = [
  { value: "United States", label: "United States" },
  { value: "Canada", label: "Canada" },
];

function ClinicDetails({ clinic, onUpdateClinic, editMode = false }) {
  const dispatch = useDispatch();
  const { clinics = [] } = useSelector((state) => state.clinics);
  const [saveStatus, setSaveStatus] = useState('initial');
  
  // Payment method management state
  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = useState(false);
  const [showCopyPaymentMethodsModal, setShowCopyPaymentMethodsModal] = useState(false);
  const [paymentMethodForm, setPaymentMethodForm] = useState({
    englishDescription: ''
  });
  const [copySettings, setCopySettings] = useState({
    sourceClinic: null,
    replaceExisting: false
  });
  const [operationLoading, setOperationLoading] = useState(false);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    paymentMethod: null
  });

  console.log('[Clinic Details] Received clinic prop:', {
    clinicId: clinic?._id,
    clinicName: clinic?.name,
    paymentMethodsCount: clinic?.paymentMethods?.length || 0
  });

  if (!clinic) {
    logInfo("No clinic provided to ClinicDetails component");
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-sm text-gray-600">No clinic selected.</div>
      </div>
    );
  }

  // Use the clinic prop directly - no Redux state confusion
  const currentClinic = clinic;

  logInfo("Rendering clinic details", {
    clinicId: currentClinic._id,
    clinicName: currentClinic.name,
  });

  const formatHours = (hours) => {
    if (!hours || !hours.open || !hours.close) {
      return "Closed";
    }
    return `${hours.open} - ${hours.close}`;
  };

  // Payment method management functions
  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    
    if (!paymentMethodForm.englishDescription || !paymentMethodForm.englishDescription.trim()) {
      toast.warning('Payment method description is required');
      return;
    }
    
    if (paymentMethodForm.englishDescription.length > 31) {
      toast.warning('Description must be 31 characters or less');
      return;
    }
    
    setOperationLoading(true);
    try {
      const updatedClinic = await dispatch(addPaymentMethodToClinic({
        clinicId: currentClinic._id,
        paymentMethodData: paymentMethodForm
      })).unwrap();
      
      // Use the updated clinic from the result
      onUpdateClinic(updatedClinic);
      
      toast.success('Payment method added successfully');
      setShowAddPaymentMethodModal(false);
      setPaymentMethodForm({ englishDescription: '' });
    } catch (error) {
      toast.error(`Failed to add payment method: ${error?.error || error?.message || error}`);
    } finally {
      setOperationLoading(false);
    }
  };
  
  const handleRemovePaymentMethod = (paymentMethod) => {
    setConfirmDialog({
      open: true,
      title: 'Remove Payment Method',
      message: `Are you sure you want to remove "${paymentMethod.englishDescription}"? This action cannot be undone.`,
      paymentMethod,
      onConfirm: async () => {
        setOperationLoading(true);
        try {
          const updatedClinic = await dispatch(removePaymentMethodFromClinic({
            clinicId: currentClinic._id,
            paymentMethodId: paymentMethod._id
          })).unwrap();
          
          // Use the updated clinic from the result
          onUpdateClinic(updatedClinic);
          
          toast.success('Payment method removed successfully');
        } catch (error) {
          toast.error(`Failed to remove payment method: ${error?.error || error?.message || error}`);
        } finally {
          setOperationLoading(false);
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, paymentMethod: null });
        }
      }
    });
  };
  
  const handleCopyPaymentMethods = async (e) => {
    e.preventDefault();
    
    if (!copySettings.sourceClinic) {
      toast.warning('Please select a source clinic to copy from');
      return;
    }
    
    if (copySettings.sourceClinic._id === currentClinic._id) {
      toast.warning('Cannot copy payment methods from the same clinic');
      return;
    }
    
    const confirmMessage = copySettings.replaceExisting 
      ? `This will replace all existing payment methods in "${currentClinic.name}" with methods from "${copySettings.sourceClinic.name}".`
      : `This will add payment methods from "${copySettings.sourceClinic.name}" to "${currentClinic.name}" (duplicates will be skipped).`;
    
    setConfirmDialog({
      open: true,
      title: 'Copy Payment Methods',
      message: confirmMessage + ' This action cannot be undone.',
      onConfirm: async () => {
        await performCopyOperation();
      }
    });
  };
  
  const performCopyOperation = async () => {
    setOperationLoading(true);
    try {
      const result = await dispatch(copyPaymentMethodsBetweenClinics({
        targetClinicId: currentClinic._id,
        sourceClinicId: copySettings.sourceClinic._id,
        replaceExisting: copySettings.replaceExisting
      })).unwrap();
      
      // Use the updated clinic from the result
      onUpdateClinic(result.clinic);
      
      toast.success(
        `Payment methods copied successfully. ${result.copiedCount} methods copied` +
        (result.skippedCount > 0 ? `, ${result.skippedCount} duplicates skipped` : '')
      );
      setShowCopyPaymentMethodsModal(false);
      setCopySettings({ sourceClinic: null, replaceExisting: false });
    } catch (error) {
      toast.error(`Failed to copy payment methods: ${error}`);
    } finally {
      setOperationLoading(false);
      setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, paymentMethod: null });
    }
  };

  const handleSubmit = async (values) => {
    setSaveStatus('saving');
    try {
      console.log('[Clinic Details] Form submission values:', values);
      console.log('[Clinic Details] Current clinic payment methods:', currentClinic.paymentMethods);
      
      // The payment methods should be submitted as-is since they're already complete objects
      const updatedValues = {
        ...values,
        // Keep payment methods as they are - they should already be the complete objects from the database
      };
      
      console.log('[Clinic Details] Submitting updated values:', updatedValues);
      await onUpdateClinic(updatedValues);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('initial'), 2000);
    } catch (error) {
      console.error('[Clinic Details] Form submission error:', error);
      setSaveStatus('error');
    }
  };
  
  // Get other clinics for copying (exclude current clinic)
  const otherClinics = clinics.filter(c => c._id !== currentClinic._id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Clinic Details</h2>
          <p className="text-sm text-gray-500 mt-1">Basic clinic information and settings</p>
        </div>
      </div>

      <Formik
        initialValues={currentClinic}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Basic Information</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Clinic Name *
                  </label>
                  {editMode ? (
                    <Field
                      name="name"
                      type="text"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.name}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  {editMode ? (
                    <Field
                      name="email"
                      type="email"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.email}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  {editMode ? (
                    <Field
                      name="phoneNumber"
                      type="tel"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.phoneNumber}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  {editMode ? (
                    <Field
                      name="website"
                      type="url"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.website || 'Not specified'}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="invoicePrefix" className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Prefix *
                  </label>
                  {editMode ? (
                    <Field
                      name="invoicePrefix"
                      type="text"
                      maxLength={4}
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent uppercase"
                      style={{textTransform: 'uppercase'}}
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.invoicePrefix || 'Not specified'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Address</h3>
              {editMode ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                    <Field
                      name="address.street"
                      type="text"
                      placeholder="123 Main Street"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                      <Field
                        name="address.city"
                        type="text"
                        placeholder={values.address?.country === "United States" ? "New York" : "Toronto"}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {getAddressLabels(values.address?.country).regionLabel} *
                      </label>
                      <Field name="address.state">
                        {({ field, form }) => {
                          const isCanada = values.address?.country === "Canada";
                          const regions = isCanada ? canadianProvinces : usStates;
                          return (
                            <select
                              {...field}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            >
                              <option value="">{isCanada ? "Select Province" : "Select State"}</option>
                              {regions.map((region) => (
                                <option key={region.value} value={region.value}>
                                  {region.label}
                                </option>
                              ))}
                            </select>
                          );
                        }}
                      </Field>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {getAddressLabels(values.address?.country).postalLabel} *
                      </label>
                      <Field
                        name="address.zipCode"
                        type="text"
                        placeholder={values.address?.country === "Canada" ? "M5V 3A8" : "12345"}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                      <Field name="address.country">
                        {({ field, form }) => (
                          <select
                            {...field}
                            onChange={(e) => {
                              const newCountry = e.target.value;
                              form.setFieldValue("address.country", newCountry);
                              // Clear state when country changes
                              if ((newCountry === "United States" && values.address?.country !== "United States") ||
                                  (newCountry === "Canada" && values.address?.country !== "Canada")) {
                                form.setFieldValue("address.state", "");
                              }
                            }}
                            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          >
                            {countries.map((country) => (
                              <option key={country.value} value={country.value}>
                                {country.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                  {values.address?.street && (
                    <>
                      {values.address.street}<br />
                      {values.address.city}, {values.address.state} {values.address.zipCode}<br />
                      {values.address.country}
                    </>
                  ) || 'No address specified'}
                </div>
              )}
            </div>

            {/* US Insurance Billing Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Insurance Billing Information</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label htmlFor="facilityNPI" className="block text-sm font-medium text-gray-700 mb-1">Facility NPI</label>
                  {editMode ? (
                    <Field name="facilityNPI" type="text" maxLength="10" placeholder="10-digit NPI"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.facilityNPI || 'Not set'}
                    </div>
                  )}
                </div>
                <div>
                  <label htmlFor="taxIdEIN" className="block text-sm font-medium text-gray-700 mb-1">Tax ID / EIN</label>
                  {editMode ? (
                    <Field name="taxIdEIN" type="text" placeholder="XX-XXXXXXX"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.taxIdEIN || 'Not set'}
                    </div>
                  )}
                </div>
                <div>
                  <label htmlFor="taxIdType" className="block text-sm font-medium text-gray-700 mb-1">Tax ID Type</label>
                  {editMode ? (
                    <Field as="select" name="taxIdType"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent">
                      <option value="">Select...</option>
                      <option value="EIN">EIN</option>
                      <option value="SSN">SSN</option>
                    </Field>
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.taxIdType || 'Not set'}
                    </div>
                  )}
                </div>
                <div>
                  <label htmlFor="defaultPlaceOfService" className="block text-sm font-medium text-gray-700 mb-1">Default Place of Service</label>
                  {editMode ? (
                    <Field name="defaultPlaceOfService" type="text" maxLength="2" placeholder="11"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.defaultPlaceOfService || '11'}
                    </div>
                  )}
                </div>
                <div>
                  <label htmlFor="taxonomyCode" className="block text-sm font-medium text-gray-700 mb-1">Taxonomy Code</label>
                  {editMode ? (
                    <Field name="taxonomyCode" type="text" placeholder="e.g., 261QA0600X"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.taxonomyCode || 'Not set'}
                    </div>
                  )}
                </div>
                <div>
                  <label htmlFor="billingPhone" className="block text-sm font-medium text-gray-700 mb-1">Billing Phone</label>
                  {editMode ? (
                    <Field name="billingPhone" type="tel" placeholder="Billing department phone"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.billingPhone || 'Not set'}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Billing Address (if different from physical)</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Street</label>
                    {editMode ? (
                      <Field name="billingAddress.street" type="text"
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                        {values.billingAddress?.street || 'Same as physical'}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                    {editMode ? (
                      <Field name="billingAddress.city" type="text"
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                        {values.billingAddress?.city || ''}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">State</label>
                    {editMode ? (
                      <Field name="billingAddress.state" type="text" maxLength="2"
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                        {values.billingAddress?.state || ''}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Zip</label>
                    {editMode ? (
                      <Field name="billingAddress.zip" type="text"
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                        {values.billingAddress?.zip || ''}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-700 mr-3">Accepts Assignment</label>
                {editMode ? (
                  <Field name="acceptsAssignment" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                ) : (
                  <span className="text-sm text-gray-900">{values.acceptsAssignment !== false ? 'Yes' : 'No'}</span>
                )}
              </div>
            </div>

            {/* Hours of Operation */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Hours of Operation</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(values.hoursOfOperation || {}).map(([day, hours]) => (
                  <div key={day} className="flex items-center space-x-2">
                    <span className="w-20 text-sm font-medium text-gray-700">{day}:</span>
                    {editMode ? (
                      <div className="flex items-center space-x-1 flex-1">
                        <Field
                          name={`hoursOfOperation.${day}.open`}
                          type="time"
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <span className="text-gray-500">-</span>
                        <Field
                          name={`hoursOfOperation.${day}.close`}
                          type="time"
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-gray-900">{formatHours(hours)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">
                  Payment Methods
                  <span className="ml-2 text-xs text-gray-500">
                    ({currentClinic.paymentMethods?.length || 0} configured)
                  </span>
                </h3>
                {editMode && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCopyPaymentMethodsModal(true)}
                      className="inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                      title="Copy from another clinic"
                    >
                      <DocumentDuplicateIcon className="h-3 w-3" />
                      Copy
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddPaymentMethodModal(true)}
                      className="inline-flex items-center gap-1.5 rounded border border-indigo-600 bg-indigo-600 px-2 py-1 text-xs font-medium text-white shadow-sm transition-colors hover:bg-indigo-500"
                    >
                      <PlusIcon className="h-3 w-3" />
                      Add
                    </button>
                  </div>
                )}
              </div>
              
              {editMode ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Manage payment methods for this clinic:
                  </p>
                  {currentClinic.paymentMethods?.length > 0 ? (
                    <div className="space-y-2">
                      {currentClinic.paymentMethods.map((method, index) => (
                        <div key={method._id || method.value || index} 
                             className="flex items-center justify-between p-3 border border-gray-200 rounded-sm bg-gray-50">
                          <span className="text-sm font-medium text-gray-900">{method.englishDescription}</span>
                          <button
                            type="button"
                            onClick={() => handleRemovePaymentMethod(method)}
                            disabled={operationLoading}
                            className="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50 disabled:opacity-50"
                          >
                            <TrashIcon className="h-3 w-3" />
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 border border-gray-200 rounded-sm bg-gray-50">
                      <p className="text-sm text-gray-500">No payment methods configured.</p>
                      <p className="text-xs text-gray-400 mt-1">Click "Add" to create a new payment method.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                  {currentClinic.paymentMethods?.length > 0 ? (
                    <div className="space-y-1">
                      {currentClinic.paymentMethods.map((method, index) => (
                        <div key={method._id || method.value || index}>
                          <span>{method.englishDescription}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-amber-600">No payment methods configured</span>
                  )}
                </div>
              )}
            </div>

            {/* Save Button - only show when in edit mode */}
            {editMode && (
              <div className="flex justify-end pt-4 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={saveStatus === 'saving'}
                  className={`inline-flex justify-center px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                    saveStatus === 'saving'
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : saveStatus === 'saved'
                      ? 'bg-green-600 text-white'
                      : saveStatus === 'error'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {saveStatus === 'saving' ? 'Saving...' :
                    saveStatus === 'saved' ? 'Saved!' :
                    saveStatus === 'error' ? 'Error' : 'Save Changes'}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>

      {/* Add Payment Method Modal */}
      <Transition appear show={showAddPaymentMethodModal} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowAddPaymentMethodModal(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden bg-white p-6 align-middle shadow-xl transition-all rounded-sm">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    Add Payment Method
                  </Dialog.Title>
                  
                  <form onSubmit={handleAddPaymentMethod} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method Description * (max 31 characters)
                      </label>
                      <input
                        type="text"
                        value={paymentMethodForm.englishDescription}
                        onChange={(e) => setPaymentMethodForm(prev => ({ ...prev, englishDescription: e.target.value }))}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="e.g., Visa - In Person, Cash, Interac Debit"
                        maxLength={31}
                        required
                        autoFocus
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {paymentMethodForm.englishDescription.length}/31 characters
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddPaymentMethodModal(false);
                          setPaymentMethodForm({ englishDescription: '' });
                        }}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={operationLoading}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {operationLoading ? 'Adding...' : 'Add Payment Method'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Copy Payment Methods Modal */}
      <Transition appear show={showCopyPaymentMethodsModal} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowCopyPaymentMethodsModal(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden bg-white p-6 align-middle shadow-xl transition-all rounded-sm">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    Copy Payment Methods
                  </Dialog.Title>
                  
                  <form onSubmit={handleCopyPaymentMethods} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Copy payment methods from:
                      </label>
                      <Listbox value={copySettings.sourceClinic} onChange={(clinic) => setCopySettings(prev => ({ ...prev, sourceClinic: clinic }))}>
                        <div className="relative">
                          <Listbox.Button className="relative w-full cursor-pointer rounded-sm border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                            <span className="block truncate">
                              {copySettings.sourceClinic ? copySettings.sourceClinic.name : "Select a clinic..."}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={React.Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-sm border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                              {otherClinics.map((clinic) => (
                                <Listbox.Option
                                  key={clinic._id}
                                  value={clinic}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                      active ? "bg-indigo-50 text-indigo-900" : "text-gray-700"
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                        {clinic.name} ({clinic.paymentMethods?.length || 0} methods)
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                          <CheckIcon className="h-4 w-4" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                              {otherClinics.length === 0 && (
                                <div className="py-2 px-3 text-gray-500 text-sm">
                                  No other clinics available
                                </div>
                              )}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>
                    
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={copySettings.replaceExisting}
                          onChange={(e) => setCopySettings(prev => ({ ...prev, replaceExisting: e.target.checked }))}
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Replace existing payment methods
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        {copySettings.replaceExisting 
                          ? "This will remove all current payment methods and replace them with the copied ones."
                          : "This will add new payment methods without removing existing ones. Duplicates will be skipped."
                        }
                      </p>
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCopyPaymentMethodsModal(false);
                          setCopySettings({ sourceClinic: null, replaceExisting: false });
                        }}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={operationLoading || !copySettings.sourceClinic}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {operationLoading ? 'Copying...' : 'Copy Payment Methods'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Custom Confirmation Dialog */}
      <Transition appear show={confirmDialog.open} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden bg-white p-6 align-middle shadow-xl transition-all rounded-sm">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    {confirmDialog.title}
                  </Dialog.Title>
                  
                  <p className="text-sm text-gray-600 mb-6">
                    {confirmDialog.message}
                  </p>
                  
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmDialog.onConfirm}
                      disabled={operationLoading}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      {operationLoading ? 'Processing...' : 'Confirm'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default ClinicDetails;